import { ReactNode, useCallback, useState } from 'react'
import { VisitorRegistrationAnswers, VisitorRegistrationRecord } from '../../../../types/Company'
import Dialog from '../../../atoms/feedback/Dialog'
import { FlexColumn, FlexRow } from '../../../atoms/layout/Flex'
import Label from '../../../atoms/display/Label'
import Divider from '../../../atoms/display/Divider'
import Grid from '../../../atoms/layout/Grid'
import useDay from '../../../../hooks/useDay'
import Card from '../../../atoms/surfaces/Card'
import IconButton from '../../../atoms/inputs/IconButton'
import { AttachmentIcon } from '../../../atoms/display/Icons'
import RecordFilePreviewDialog from './RecordFilePreviewDialog'

type Props = {
  open: boolean
  record: VisitorRegistrationRecord
  onClose: () => void
}

function SurveyRow(props: { text: string; children: ReactNode }) {
  const { text, children } = props
  return (
    <FlexColumn>
      <FlexRow>
        <FlexRow width={640}>
          <Label text={text} />
        </FlexRow>
        {children}
      </FlexRow>
      <Divider />
    </FlexColumn>
  )
}

// ファイルプレビューボタン
type FilePreviewButtonProps = {
  fileId: string | undefined
  fileName: string | undefined
  onOpenPreview: (fileId: string, fileName: string) => void
}

function FilePreviewButton({ fileId, fileName, onOpenPreview }: FilePreviewButtonProps): JSX.Element | null {
  if (fileId === undefined || fileName === undefined) {
    return null
  }

  return (
    <IconButton label="ファイルを表示" onClick={() => onOpenPreview(fileId, fileName)}>
      <AttachmentIcon />
    </IconButton>
  )
}

function isWorkCategoryEmpty(answers: VisitorRegistrationAnswers) {
  return (
    !answers.workCategory1 &&
    !answers.workCategory2 &&
    !answers.workCategory3 &&
    !answers.workCategory4 &&
    !answers.workCategory5 &&
    !answers.workCategory6 &&
    !answers.workCategory7 &&
    !answers.workCategory8 &&
    !answers.workCategory9 &&
    !answers.workCategory10 &&
    !answers.workCategory11 &&
    !answers.workCategory12 &&
    !answers.workCategory13 &&
    !answers.workCategoryOther
  )
}

function isQualificationAEmpty(answers: VisitorRegistrationAnswers) {
  return (
    !answers.qualificationA1 &&
    !answers.qualificationA2 &&
    !answers.qualificationA3 &&
    !answers.qualificationA4 &&
    !answers.qualificationA5 &&
    !answers.qualificationA6 &&
    !answers.qualificationA7 &&
    !answers.qualificationA8 &&
    !answers.qualificationAOther
  )
}

function isQualificationBEmpty(answers: VisitorRegistrationAnswers) {
  return (
    !answers.qualificationB1 &&
    !answers.qualificationB2 &&
    !answers.qualificationB3 &&
    !answers.qualificationB4 &&
    !answers.qualificationB5 &&
    !answers.qualificationB6 &&
    !answers.qualificationB7 &&
    !answers.qualificationB8 &&
    !answers.qualificationBOther
  )
}

function isQualificationCEmpty(answers: VisitorRegistrationAnswers) {
  return (
    !answers.qualificationC1 && !answers.qualificationC2 && !answers.qualificationC3 && !answers.qualificationCOther
  )
}

function isQualificationDEmpty(answers: VisitorRegistrationAnswers) {
  return (
    !answers.qualificationD1 &&
    !answers.qualificationD2 &&
    !answers.qualificationD3 &&
    !answers.qualificationD4 &&
    !answers.qualificationD5 &&
    !answers.qualificationD6 &&
    !answers.qualificationD7 &&
    !answers.qualificationD8 &&
    !answers.qualificationD9 &&
    !answers.qualificationBOther
  )
}

export default function VisitorRegistrationRecordConfirmDialog(props: Props) {
  const { open, record, onClose } = props
  const day = useDay()

  // プレビューダイアログの状態
  const [previewFile, setPreviewFile] = useState<{ id: string; name: string } | null>(null)

  // プレビューダイアログを開く
  const handleOpenPreview = useCallback((fileId: string, fileName: string) => {
    setPreviewFile({ id: fileId, name: fileName })
  }, [])

  // プレビューダイアログを閉じる
  const handleClosePreview = useCallback(() => {
    setPreviewFile(null)
  }, [])

  return (
    <Dialog open={open} width="md" title="新規入場者調査票" onClose={onClose}>
      <FlexColumn space={2}>
        <Card size="full">
          <FlexColumn>
            <Grid columns={['160px', '1fr', '120px']}>
              <Label text="事業者名" variant="subtitle" />
              <Label text="住所" variant="subtitle" />
              <Label text="電話番号" variant="subtitle" />
              <FlexColumn space={0}>
                <Label text={record.companyKana} variant="caption" />
                <Label text={record.companyName} />
              </FlexColumn>
              <FlexColumn space={0}>
                <FlexRow space={0}>
                  <Label text="〒" variant="caption" />
                  <Label text={record.companyZipCode} variant="caption" />
                </FlexRow>
                <Label text={record.companyAddress} />
              </FlexColumn>
              <Label text={record.companyPhoneNumber} />
            </Grid>
            <Grid columns={['160px', '80px', '180px']}>
              <Label text="代表者氏名" variant="subtitle" />
              <Label text="血液型" variant="subtitle" />
              <Label text="生年月日" variant="subtitle" />
              <FlexColumn space={0}>
                <Label text={record.representativeKana} variant="caption" />
                <Label text={record.representativeName} />
              </FlexColumn>
              <Label text={`${record.representativeBloodType}型`} />
              <FlexRow>
                <Label text={day.formatDateJa(record.representativeBirthDate)} />
                <Label text={`${day.toAge(record.representativeBirthDate)}歳`} />
              </FlexRow>
            </Grid>
          </FlexColumn>
        </Card>
        <SurveyRow text="（個人事業主の方に質問）あなたは一人親方ですか">
          <Label text={{ 1: '法人', 2: '個人' }[record.answers.survey1] ?? ''} />
        </SurveyRow>
        <SurveyRow text="労働者は何人いますか">
          <Label text={{ 1: '5人以上', 2: '5人未満' }[record.answers.survey2] ?? ''} />
        </SurveyRow>
        <SurveyRow text="使用する労働者数が年間延べ100人以上となりますか">
          <Label text={{ 1: 'はい', 2: 'いいえ' }[record.answers.survey3] ?? ''} />
        </SurveyRow>
        <SurveyRow text="（個人事業主の方に質問）特別加入労災保険に加入していますか？">
          <FlexRow align="center">
            <Label text={{ 1: '加入', 2: '未加入' }[record.answers.survey4] ?? ''} />
            <FilePreviewButton
              fileId={record.answers.survey4FileId}
              fileName={record.answers.survey4FileName}
              onOpenPreview={handleOpenPreview}
            />
          </FlexRow>
        </SurveyRow>
        <SurveyRow text="（従業員のいる方に質問）労災保険に加入していますか？">
          <FlexRow align="center">
            <Label text={{ 1: '加入', 2: '未加入' }[record.answers.survey16] ?? ''} />
            <FilePreviewButton
              fileId={record.answers.survey16FileId}
              fileName={record.answers.survey16FileName}
              onOpenPreview={handleOpenPreview}
            />
          </FlexRow>
        </SurveyRow>
        <SurveyRow text="法定外労災補償制度に加入していますか">
          <Label text={{ 1: '加入', 2: '未加入' }[record.answers.survey5] ?? ''} />
        </SurveyRow>
        <SurveyRow text="健康保険に加入していますか">
          <FlexColumn>
            <Label text={{ 1: '加入', 2: '未加入' }[record.answers.survey6] ?? ''} />
            <Label text={record.answers.survey6Name ?? ''} />
          </FlexColumn>
        </SurveyRow>
        <SurveyRow text="年金保険に加入していますか">
          <FlexColumn>
            <Label text={{ 1: '加入', 2: '未加入' }[record.answers.survey7] ?? ''} />
            <Label text={record.answers.survey7Name ?? ''} />
          </FlexColumn>
        </SurveyRow>
        <SurveyRow text="雇用保険に加入していますか">
          <Label text={{ 1: '加入', 2: '未加入' }[record.answers.survey8] ?? ''} />
        </SurveyRow>
        <SurveyRow text="賠償責任保険に加入していますか">
          <FlexRow align="center">
            <Label text={{ 1: '加入', 2: '未加入' }[record.answers.survey9] ?? ''} />
            <FilePreviewButton
              fileId={record.answers.survey9FileId}
              fileName={record.answers.survey9FileName}
              onOpenPreview={handleOpenPreview}
            />
          </FlexRow>
        </SurveyRow>
        <SurveyRow text="建設現場で働き始めたのは何年頃ですか">
          <Label text={`西暦${record.answers.survey10}年頃`} />
        </SurveyRow>
        <SurveyRow text="毎年健康診断を受けていますか">
          <Label text={{ 1: 'はい', 2: 'いいえ' }[record.answers.survey11] ?? ''} />
        </SurveyRow>
        <SurveyRow text="労働者を雇入する際に雇入時の全項目の健康診断並びに毎年1回の定期健康診断を実施し結果票を5年間保管していますか">
          <Label text={{ 1: 'はい', 2: 'いいえ' }[record.answers.survey12] ?? ''} />
        </SurveyRow>
        <SurveyRow text="労働者に対して送り出し教育を実施していますか">
          <Label text={{ 1: 'はい', 2: 'いいえ' }[record.answers.survey13] ?? ''} />
        </SurveyRow>
        <SurveyRow text="労働者に対して運転免許停止など処分の有無を適宜確認していますか">
          <Label text={{ 1: 'はい', 2: 'いいえ' }[record.answers.survey14] ?? ''} />
        </SurveyRow>
        <SurveyRow text="工事を下請に任せることはありますか">
          <Label text={{ 1: 'はい', 2: 'いいえ' }[record.answers.survey15] ?? ''} />
        </SurveyRow>
        <Label text="対応可能工種" variant="title" />
        <FlexRow wrap>
          {isWorkCategoryEmpty(record.answers) && <Label text="なし" />}
          {record.answers.workCategory1 && <Label text="屋根" />}
          {record.answers.workCategory2 && <Label text="外壁" />}
          {record.answers.workCategory3 && <Label text="足場" />}
          {record.answers.workCategory4 && <Label text="太陽光" />}
          {record.answers.workCategory5 && <Label text="電化" />}
          {record.answers.workCategory6 && <Label text="蓄電池" />}
          {record.answers.workCategory7 && <Label text="浄化槽" />}
          {record.answers.workCategory8 && <Label text="外構" />}
          {record.answers.workCategory9 && <Label text="基礎" />}
          {record.answers.workCategory10 && <Label text="内装" />}
          {record.answers.workCategory11 && <Label text="水回り" />}
          {record.answers.workCategory12 && <Label text="床下" />}
          {record.answers.workCategory13 && <Label text="増改築" />}
          {record.answers.workCategoryOther && <Label text={record.answers.workCategoryOtherText ?? ''} />}
        </FlexRow>
        <Label text="国家資格" variant="title" />
        <FlexRow wrap>
          {isQualificationAEmpty(record.answers) && <Label text="なし" />}
          {record.answers.qualificationA1 && <Label text="1級建築士" />}
          {record.answers.qualificationA2 && <Label text="2級建築士" />}
          {record.answers.qualificationA3 && <Label text="木造建築士" />}
          {record.answers.qualificationA4 && <Label text="1級施工管理技士" />}
          {record.answers.qualificationA5 && <Label text="2級施工管理技士" />}
          {record.answers.qualificationA6 && <Label text="1級塗装" />}
          {record.answers.qualificationA7 && <Label text="1種電気" />}
          {record.answers.qualificationA8 && <Label text="2種電気" />}
          {record.answers.qualificationAOther && <Label text={record.answers.qualificationAOtherText ?? ''} />}
        </FlexRow>
        <Label text="技能講習（作業主任者）" variant="title" />
        <FlexRow wrap>
          {isQualificationBEmpty(record.answers) && <Label text="なし" />}
          {record.answers.qualificationB1 && <Label text="足場の組立て等" />}
          {record.answers.qualificationB2 && <Label text="有機溶剤" />}
          {record.answers.qualificationB3 && <Label text="石綿" />}
          {record.answers.qualificationB4 && <Label text="酸素欠乏危険" />}
          {record.answers.qualificationB5 && <Label text="土止め支保工" />}
          {record.answers.qualificationB6 && <Label text="地山掘削" />}
          {record.answers.qualificationB7 && <Label text="玉掛け" />}
          {record.answers.qualificationB8 && <Label text="コンクリート破砕" />}
          {record.answers.qualificationBOther && <Label text={record.answers.qualificationBOtherText ?? ''} />}
        </FlexRow>
        <Label text="技能講習（運転者）" variant="title" />
        <FlexRow wrap>
          {isQualificationCEmpty(record.answers) && <Label text="なし" />}
          {record.answers.qualificationC1 && <Label text="車両系建設機械（3t以上）" />}
          {record.answers.qualificationC2 && <Label text="小型移動式クレーン（1t以上5t未満）" />}
          {record.answers.qualificationC3 && <Label text="フォークリフト（1t以上）" />}
          {record.answers.qualificationCOther && <Label text={record.answers.qualificationCOtherText ?? ''} />}
        </FlexRow>
        <Label text="特別教育（作業者・運転者）" variant="title" />
        <FlexRow wrap>
          {isQualificationDEmpty(record.answers) && <Label text="なし" />}
          {record.answers.qualificationD1 && <Label text="足場組立従者" />}
          {record.answers.qualificationD2 && <Label text="フルハーネス型墜落制止用器具" />}
          {record.answers.qualificationD3 && <Label text="石綿作業従事者" />}
          {record.answers.qualificationD4 && <Label text="玉掛け（1t未満）" />}
          {record.answers.qualificationD5 && <Label text="自由研削といし（グライダー）" />}
          {record.answers.qualificationD6 && <Label text="丸のこ等取扱い作業" />}
          {record.answers.qualificationD7 && <Label text="フォークリフト（1t未満）" />}
          {record.answers.qualificationD8 && <Label text="小型車両系建設機械（3t未満）" />}
          {record.answers.qualificationD9 && <Label text="巻上げ機運転（ウインチ）" />}
          {record.answers.qualificationDOther && <Label text={record.answers.qualificationDOtherText ?? ''} />}
        </FlexRow>
        <Label text="労働者" variant="title" />
        {record.workers.length === 0 && <Label text="なし" />}
        {record.workers.map((worker) => (
          <Card key={worker.id} size="full">
            <FlexColumn>
              <Grid columns={['160px', '60px', '60px', '180px', '120px']}>
                <Label text="氏名" variant="subtitle" />
                <Label text="性別" variant="subtitle" />
                <Label text="血液型" variant="subtitle" />
                <Label text="生年月日" variant="subtitle" />
                <Label text="電話番号" variant="subtitle" />
                <FlexColumn space={0}>
                  <Label text={worker.kana} variant="caption" />
                  <Label text={worker.name} />
                </FlexColumn>
                <Label text={{ 1: '男性', 2: '女性', 3: 'その他' }[worker.gender]} />
                <Label text={`${worker.bloodType}型`} />
                <FlexRow>
                  <Label text={day.formatDateJa(worker.birthDate)} />
                  <Label text={`${day.toAge(worker.birthDate)}歳`} />
                </FlexRow>
                <Label text={worker.phoneNumber} />
              </Grid>
              <Grid columns={['1fr', '1fr']}>
                <Label text="住所" variant="subtitle" />
                <Label text="保有資格" variant="subtitle" />
                <FlexColumn space={0}>
                  <FlexRow space={0}>
                    <Label text="〒" variant="caption" />
                    <Label text={worker.zipCode} variant="caption" />
                  </FlexRow>
                  <Label text={worker.address} />
                </FlexColumn>
                <Label text={worker.qualification ?? 'なし'} />
              </Grid>
              <Grid columns={['1fr', '1fr', '1fr']}>
                <Label text="健康保険" variant="subtitle" />
                <Label text="年金保険" variant="subtitle" />
                <Label text="雇用保険" variant="subtitle" />
                <FlexRow>
                  <Label text={worker.healthInsuranceName} />
                  <Label text="下4桁" />
                  <Label text={worker.healthInsuranceLastDigits} />
                </FlexRow>
                <FlexRow>
                  <Label text={worker.pensionInsuranceName} />
                  <Label text="下4桁" />
                  <Label text={worker.pensionInsuranceLastDigits} />
                </FlexRow>
                <FlexRow>
                  <Label text="下4桁" />
                  <Label text={worker.employmentInsuranceLastDigits ?? '未入力'} />
                </FlexRow>
              </Grid>
            </FlexColumn>
          </Card>
        ))}
        <Label text="下請け" variant="title" />
        {record.subcontractors.length === 0 && <Label text="なし" />}
        {record.subcontractors.map((subcontractor) => (
          <Card key={subcontractor.id} size="full">
            <FlexColumn>
              <Grid columns={['320px', '160px', '120px']}>
                <Label text="事業者名" variant="subtitle" />
                <Label text="代表者氏名" variant="subtitle" />
                <Label text="電話番号" variant="subtitle" />
                <FlexColumn space={0}>
                  <Label text={subcontractor.kana} variant="caption" />
                  <Label text={subcontractor.name} />
                </FlexColumn>
                <FlexColumn space={0}>
                  <Label text={subcontractor.representativeKana} variant="caption" />
                  <Label text={subcontractor.representativeName} />
                </FlexColumn>
                <Label text={subcontractor.phoneNumber} />
              </Grid>
              <FlexColumn space={0}>
                <Label text="住所" variant="subtitle" />
                <FlexColumn space={0}>
                  <FlexRow space={0}>
                    <Label text="〒" variant="caption" />
                    <Label text={subcontractor.zipCode} variant="caption" />
                  </FlexRow>
                  <Label text={subcontractor.address} />
                </FlexColumn>
              </FlexColumn>
            </FlexColumn>
          </Card>
        ))}
      </FlexColumn>

      {/* ファイルプレビューダイアログ */}
      <RecordFilePreviewDialog file={previewFile} recordId={record.id} onClose={handleClosePreview} />
    </Dialog>
  )
}
